import React, { createContext, useState, useContext, useCallback, useEffect, useRef, ReactNode } from "react";
import { useUser } from "./UserContext";
import { getProgress, updateChecks, updateProgress } from "../services/databaseService";
import { TOTAL_SOLUTIONS_SECTIONS, TOTAL_DESKTOP_SECTIONS } from "../constants/trackingSections";

interface UserProgressContextType {
  solutionsChecks: Set<string>;
  desktopChecks: Set<string>;
  solutionsProgress: number;
  desktopProgress: number;
  trackSection: (type: "solutions" | "desktop", sectionId: string) => void;
}

const UserProgressContext = createContext<UserProgressContextType | undefined>(undefined);

export const UserProgressProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { userId } = useUser();
  const [solutionsChecks, setSolutionsChecks] = useState<Set<string>>(new Set());
  const [desktopChecks, setDesktopChecks] = useState<Set<string>>(new Set());
  const [solutionsProgress, setSolutionsProgress] = useState(0);
  const [desktopProgress, setDesktopProgress] = useState(0);
  const loadedRef = useRef(false);
  const loadingRef = useRef(false);

  useEffect(() => {
    if (!userId || loadedRef.current || loadingRef.current) return;
    loadingRef.current = true;

    getProgress(userId)
      .then((data) => {
        setSolutionsChecks(new Set(data.solutionsChecks || []));
        setDesktopChecks(new Set(data.desktopChecks || []));
        setSolutionsProgress(data.solutions || 0);
        setDesktopProgress(data.desktop || 0);
        loadedRef.current = true;
      })
      .catch(() => {
        loadedRef.current = true;
      })
      .finally(() => {
        loadingRef.current = false;
      });
  }, [userId]);

  const trackSection = useCallback(
    (type: "solutions" | "desktop", sectionId: string) => {
      if (!userId || !loadedRef.current) return;

      try {
        if (type === "solutions") {
          setSolutionsChecks((prev) => {
            if (prev.has(sectionId)) return prev;
            const next = new Set(prev);
            next.add(sectionId);
            const newChecks = Array.from(next);
            const pct = Math.round((newChecks.length / TOTAL_SOLUTIONS_SECTIONS) * 100);
            setSolutionsProgress(pct);
            updateChecks(userId, "solutions", newChecks).catch(() => {});
            updateProgress(userId, "solutions", pct).catch(() => {});
            return next;
          });
        } else {
          setDesktopChecks((prev) => {
            if (prev.has(sectionId)) return prev;
            const next = new Set(prev);
            next.add(sectionId);
            const newChecks = Array.from(next);
            const pct = Math.round((newChecks.length / TOTAL_DESKTOP_SECTIONS) * 100);
            setDesktopProgress(pct);
            updateChecks(userId, "desktop", newChecks).catch(() => {});
            updateProgress(userId, "desktop", pct).catch(() => {});
            return next;
          });
        }
      } catch {
      }
    },
    [userId]
  );

  return (
    <UserProgressContext.Provider
      value={{ solutionsChecks, desktopChecks, solutionsProgress, desktopProgress, trackSection }}
    >
      {children}
    </UserProgressContext.Provider>
  );
};

export const useUserProgress = () => {
  const context = useContext(UserProgressContext);
  if (context === undefined) {
    throw new Error("useUserProgress must be used within a UserProgressProvider");
  }
  return context;
};
